import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { formatZAR } from '../utils/currency';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { Product } from '../types/models';
import { Toast } from './ui/Toast';
interface ProductCardProps {
  product?: Product;
  name: string;
  price: number;
  image: string;
  category: string;
  isNew?: boolean;
}
export function ProductCard({
  product,
  name,
  price,
  image,
  category,
  isNew
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const {
    toggleFavorite,
    isFavorite
  } = useFavorites();
  const {
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (product) {
      addToCart(product);
      setShowToast(true);
    }
  };
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (product) {
      toggleFavorite(product.id);
    }
  };
  const isProductFavorite = product ? isFavorite(product.id) : false;
  return <>
      <Toast isVisible={showToast} productName={name} onClose={() => setShowToast(false)} onViewCart={() => navigate('/cart')} data-id="element-965" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} whileHover={{
      y: -5
    }} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300" data-id="element-966">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-gray-100" data-id="element-967">
          <img src={image} alt={name} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" data-id="element-968" />

          {/* Badges */}
          {isNew && <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-rose-500 shadow-sm" data-id="element-969">
              NEW
            </div>}

          {/* Quick Actions */}
          <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 transform translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" data-id="element-970">
            <button onClick={handleToggleFavorite} className={`p-2 bg-white rounded-full shadow-md transition-colors ${isProductFavorite ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'}`} data-id="element-971">
              <Heart className={`w-5 h-5 ${isProductFavorite ? 'fill-current' : ''}`} data-id="element-972" />
            </button>
          </div>

          <div className="absolute bottom-4 right-4 opacity-0 transform translate-y-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0" data-id="element-973">
            <button onClick={handleAddToCart} className="p-3 bg-rose-500 rounded-full shadow-lg text-white hover:bg-rose-600 transition-colors" title="Add to Cart" data-id="element-974">
              <ShoppingCart className="w-5 h-5" data-id="element-975" />
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="p-5" data-id="element-976">
          <div className="text-xs font-medium text-rose-500 mb-1 uppercase tracking-wider" data-id="element-977">
            {category}
          </div>
          <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 group-hover:text-rose-600 transition-colors line-clamp-1" data-id="element-978">
            {name}
          </h3>
          <div className="flex items-center justify-between" data-id="element-979">
            <span className="text-lg font-medium text-gray-900" data-id="element-980">
              {formatZAR(price)}
            </span>{' '}
          </div>
        </div>
      </motion.div>
    </>;
}